import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
  Tooltip,
  Typography,
} from '@material-ui/core';
import DeleteIcon from '@material-ui/icons/Delete';
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined';
import PublishIcon from '@material-ui/icons/Publish';
import { InfoCard } from '@backstage/core-components';
import { useTranslation } from 'react-i18next';
import { listContextFiles, type ProjectsApiOptions } from '../../api/projects';
import { canManageArtifacts } from './ProjectDetailsPage';
import { ContextFileStatusChip } from './ContextFileStatusChip';
import { UploadContextFileDialog } from './UploadContextFileDialog';
import { DeleteContextFileDialog } from './DeleteContextFileDialog';
import { FileDetailDialog } from './FileDetailDialog';
import {
  type ContextFileEntry,
  type ContextFileStatus,
  type Project,
} from './types';

const IN_PROGRESS_STATUSES: ContextFileStatus[] = [
  'PENDING',
  'QUEUED',
  'VALIDATING',
  'EXTRACTING',
  'INDEXING',
];

const STATUS_PROGRESSION: ContextFileStatus[] = [
  'PENDING',
  'QUEUED',
  'VALIDATING',
  'EXTRACTING',
  'INDEXING',
  'READY',
];

const POLL_INTERVAL_MS = 5000;
const PREVIEW_TICK_MS = 2000;

const useStyles = makeStyles((theme: Theme) => ({
  subtle: {
    color: theme.palette.text.secondary,
  },
  failedRow: {
    backgroundColor: `${theme.palette.error.light}22`,
  },
}));

type Props = {
  project: Project;
  apiOptions: ProjectsApiOptions;
  isPreviewMode: boolean;
};

export function ProjectFilesPanel({
  project,
  apiOptions,
  isPreviewMode,
}: Props) {
  const classes = useStyles();
  const { t } = useTranslation('projects');
  const canManage = canManageArtifacts(project);

  const [files, setFiles] = useState<ContextFileEntry[]>([]);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [versionUploadFile, setVersionUploadFile] =
    useState<ContextFileEntry | null>(null);
  const [detailFile, setDetailFile] = useState<ContextFileEntry | null>(null);
  const [fileToDelete, setFileToDelete] = useState<ContextFileEntry | null>(
    null,
  );
  const [error, setError] = useState<string | undefined>();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const hasInProgress = (entries: ContextFileEntry[]) =>
    entries.some(f => IN_PROGRESS_STATUSES.includes(f.status));

  const stopPolling = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const loadFiles = useCallback(async () => {
    setError(undefined);
    try {
      const entries = await listContextFiles(apiOptions, project.id);
      const projectFiles = entries.filter(e => e.source === 'project');
      setFiles(projectFiles);
      if (!hasInProgress(projectFiles)) {
        stopPolling();
      }
    } catch {
      setError(
        t('contextFiles.loadError', {
          defaultValue: 'Failed to load project files.',
        }),
      );
    }
  }, [apiOptions, project.id, t]);

  const startPolling = useCallback(() => {
    stopPolling();
    intervalRef.current = setInterval(() => {
      void loadFiles();
    }, POLL_INTERVAL_MS);
  }, [loadFiles]);

  useEffect(() => {
    if (isPreviewMode) {
      setFiles(
        project.artifacts.map(a => ({
          fileId: a.fileId ?? a.id,
          displayName: a.name,
          fileTypeTag: a.typeTag ?? '',
          status: a.status,
          source: 'project',
          version: 1,
          uploader: a.uploader ?? null,
          isIncluded: true,
          errorReport: a.errorDetails ?? null,
        })),
      );
    } else {
      void loadFiles();
    }
    return stopPolling;
  }, [isPreviewMode, project.artifacts, loadFiles]);

  useEffect(() => {
    if (!isPreviewMode) return undefined;

    if (!hasInProgress(files)) return undefined;

    const tick = setInterval(() => {
      setFiles(prev => {
        const updated = prev.map(f => {
          if (!IN_PROGRESS_STATUSES.includes(f.status)) return f;
          const idx = STATUS_PROGRESSION.indexOf(f.status);
          const next = STATUS_PROGRESSION[idx + 1] ?? 'READY';
          return { ...f, status: next };
        });
        if (!hasInProgress(updated)) clearInterval(tick);
        return updated;
      });
    }, PREVIEW_TICK_MS);

    return () => clearInterval(tick);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPreviewMode, files.map(f => f.fileId).join(',')]);

  useEffect(() => {
    if (!isPreviewMode && hasInProgress(files)) {
      startPolling();
    } else {
      stopPolling();
    }
    return stopPolling;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPreviewMode, files.map(f => `${f.fileId}:${f.status}`).join(',')]);

  const handleUploaded = (updatedFile: ContextFileEntry) => {
    setFiles(prev => {
      const exists = prev.some(f => f.fileId === updatedFile.fileId);
      // A new version only changes the status and version. `updatedFile` was
      // built from a snapshot taken when the dialog opened, so copying the whole
      // row would undo anything the polling or an include toggle changed since.
      return exists
        ? prev.map(f =>
            f.fileId === updatedFile.fileId
              ? {
                  ...f,
                  status: updatedFile.status,
                  version: updatedFile.version,
                }
              : f,
          )
        : [updatedFile, ...prev];
    });
  };

  const handleDeleted = (deletedFileId: string) => {
    setFiles(prev => prev.filter(f => f.fileId !== deletedFileId));
    setFileToDelete(null);
  };

  return (
    <>
      <InfoCard
        title={t('contextFiles.projectFilesTitle')}
        action={
          canManage ? (
            <Button
              color="primary"
              variant="outlined"
              size="small"
              onClick={() => setUploadOpen(true)}
            >
              {t('actions.uploadArtifact')}
            </Button>
          ) : undefined
        }
      >
        {error && (
          <Box mb={1}>
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          </Box>
        )}
        {files.length === 0 ? (
          <Typography variant="body2" className={classes.subtle}>
            {t('contextFiles.emptyProjectFiles')}
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('table.name')}</TableCell>
                <TableCell>{t('table.typeTag')}</TableCell>
                <TableCell>{t('table.status')}</TableCell>
                <TableCell>{t('table.version')}</TableCell>
                <TableCell>{t('table.uploadedBy')}</TableCell>
                <TableCell align="right">{t('table.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {files.map(file => (
                <TableRow
                  key={file.fileId}
                  className={
                    file.status === 'FAILED' ? classes.failedRow : undefined
                  }
                >
                  <TableCell>{file.displayName}</TableCell>
                  <TableCell>{file.fileTypeTag}</TableCell>
                  <TableCell>
                    <ContextFileStatusChip
                      status={file.status}
                      errorReport={file.errorReport}
                    />
                  </TableCell>
                  <TableCell>{file.version}</TableCell>
                  <TableCell>{file.uploader?.displayName ?? '—'}</TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('contextFiles.viewDetails') as string}>
                      <IconButton
                        size="small"
                        aria-label={t('contextFiles.viewDetails')}
                        onClick={() => setDetailFile(file)}
                      >
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {canManage && (
                      <Tooltip
                        title={t('contextFiles.uploadNewVersion') as string}
                      >
                        <IconButton
                          size="small"
                          aria-label={t('contextFiles.uploadNewVersion')}
                          onClick={() => setVersionUploadFile(file)}
                        >
                          <PublishIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canManage && (
                      <Tooltip title={t('contextFiles.deleteFile') as string}>
                        <IconButton
                          size="small"
                          aria-label={t('contextFiles.deleteFileNamed', {
                            name: file.displayName,
                          })}
                          onClick={() => setFileToDelete(file)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </InfoCard>

      <UploadContextFileDialog
        open={uploadOpen || !!versionUploadFile}
        onClose={() => {
          setUploadOpen(false);
          setVersionUploadFile(null);
        }}
        projectId={project.id}
        apiOptions={apiOptions}
        isPreviewMode={isPreviewMode}
        onUploaded={handleUploaded}
        targetFile={versionUploadFile}
      />

      <DeleteContextFileDialog
        file={fileToDelete}
        projectId={project.id}
        apiOptions={apiOptions}
        isPreviewMode={isPreviewMode}
        onClose={() => setFileToDelete(null)}
        onDeleted={handleDeleted}
      />

      <FileDetailDialog
        file={detailFile}
        projectId={project.id}
        apiOptions={apiOptions}
        isPreviewMode={isPreviewMode}
        canManage={canManage}
        onClose={() => setDetailFile(null)}
        onUploadNewVersion={target => {
          setDetailFile(null);
          setVersionUploadFile(target);
        }}
      />
    </>
  );
}
